import {
  GameManager,
  Size,
  Command,
  CommandType,
  Color,
  Corners,
  Entity,
  TextObject,
  V2,
  WordType,
  Word,
  VisibilityState,
  Shape,
  RenderShadow,
  DebugState,
  CORNER,
  SpriteSheet,
  PerlinController,
  GameObject,
  AnchoredPosition,
  TransformSize,
  TextObjectStyle,
  ObjectEvent,
  TransformRadius,
  RenderShapeStyle,
  type TransformFullSize,
  type User,
  type RoomUser,
} from './types';
import { SHAPE_STYLES } from './constants';
import { COLORS } from './constants-colors';
import { ImageSize, ImageSizes, Side } from './shared/api_shared_types';
import logger from './controllers/logger';
import { anchoredPos } from './entity';
import * as DataController from './controllers/data_controller';

const LERP_THRESHOLD = 0.001;

export function v2(x = 0, y = 0): V2 {
  return {
    x: x,
    y: y,
  };
}

export function cloneV2(vector: V2): V2 {
  if (vector) {
    return {
      x: vector.x,
      y: vector.y,
    };
  }

  return null;
}

export function v2IsAlmostZero(vector: V2): boolean {
  return Math.abs(vector.x) < 0.001 && Math.abs(vector.y) < 0.001;
}

export function toSize(width = 0, height = 0): Size {
  return {
    width: width,
    height: height,
  };
}

export function cloneSize(size: Size | null): Size | null {
  if (!size) return null;
  return {
    width: size.width,
    height: size.height,
  };
}

export function halfSize(size: Size): Size {
  return {
    width: size.width / 2,
    height: size.height / 2,
  };
}

export function centerPos(width: number, height: number): V2 {
  return {
    x: width / 2,
    y: height / 2,
  };
}

export function sizeToV2(size: Size): V2 {
  return v2(size.width, size.height);
}

export function getZeroCorners(): Corners {
  return [
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ];
}

export function isEvenNumber(value: number): boolean {
  return value % 2 === 0;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export function remapNumber01(
  value: number,
  min: number,
  max: number,
  clampResult: boolean,
): number {
  let t = (value - min) / (max - min);
  if (clampResult) return clamp01(t);
  return t;
}

export function remapNumber(
  value: number,
  min1: number,
  max1: number,
  min2: number,
  max2: number,
  clampResult: boolean,
): number {
  let t = (value - min1) / (max1 - min1);
  let remappedValue = min2 + t * (max2 - min2);
  if (clampResult) return clamp(remappedValue, min2, max2);
  return remappedValue;
}

export function random(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function randomPositiveOrNegativeRange(min: number, max: number): number {
  const sign = randomSign();

  return sign * random(min, max);
}

export function randomSign(): number {
  return random(-1, 1) > 0 ? 1 : -1;
}

export function randomInt(min: number, max: number): number {
  return Math.floor(random(min, max));
}

export function randomV2(minX: number, maxX: number, minY: number, maxY: number): V2 {
  let x = random(minX, maxX);
  let y = random(minY, maxY);
  return v2(x, y);
}

export function randomColor(min: number, max: number): Color {
  let r = random(min, max);
  let g = random(min, max);
  let b = random(min, max);
  return [r, g, b];
}

export function isEqual(posA: V2, posB: V2): boolean {
  return posA.x === posB.x && posA.y === posB.y;
}

export function distanceBetween(posA: V2, posB: V2): number {
  if (posA === posB) return 0;
  return Math.sqrt(Math.pow(posB.x - posA.x, 2) + Math.pow(posB.y - posA.y, 2));
}

export function distanceBetweenSqr(posA: V2, posB: V2): number {
  if (posA === posB) return 0;
  return Math.pow(posB.x - posA.x, 2) + Math.pow(posB.y - posA.y, 2);
}

export function magnitude(vector: V2): number {
  return Math.sqrt(vector.x * vector.x + vector.y * vector.y);
}

export function magnitudeSqr(vector: V2): number {
  return vector.x * vector.x + vector.y * vector.y;
}

export function magnitudeSqrXY(x, y: number): number {
  return x * x + y * y;
}

export function angleOfVector(vector: V2): number {
  let angleInRad = Math.atan(vector.y / vector.x);
  let add = vector.x < 0 ? 180 : 0;
  return (angleInRad / (2 * Math.PI)) * 360 + add;
}

export function subtractV2(vector2A: V2, vector2B: V2): V2 {
  return {
    x: vector2A.x - vector2B.x,
    y: vector2A.y - vector2B.y,
  };
}

export function addV2(...vector: V2[]): V2 {
  return {
    x: vector.reduce((total, current) => total + current.x, 0),
    y: vector.reduce((total, current) => total + current.y, 0),
  };
}

export function v2AddY(vector2: V2, addY: number): V2 {
  return {
    x: vector2.x,
    y: vector2.y + addY,
  };
}

export function v2Avarage(vectors: V2[]): V2 {
  let total = v2();
  if (vectors.length === 0) return total;
  for (let i = 0; i < vectors.length; i++) {
    total = addV2(total, vectors[i]);
  }
  return divideV2(total, vectors.length);
}

export function v2Clamp(vector: V2, minVector: V2, maxVector: V2): V2 {
  return v2(clamp(vector.x, minVector.x, maxVector.x), clamp(vector.y, minVector.y, maxVector.y));
}

export function v2AddX(vector2: V2, addX: number): V2 {
  return {
    x: vector2.x + addX,
    y: vector2.y,
  };
}

export function divideV2(vector: V2, deivider: number): V2 {
  return {
    x: vector.x / deivider,
    y: vector.y / deivider,
  };
}

export function scaleV2(vector: V2, scalar: number): V2 {
  return {
    x: vector.x * scalar,
    y: vector.y * scalar,
  };
}

// Rotates a vector about the origin by angleRad (radians, CCW), matching the
// render matrix used in canvas-worker: setTransform(cos, sin, -sin, cos, ...).
export function rotateV2(vector: V2, angleRad: number): V2 {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return {
    x: vector.x * cos - vector.y * sin,
    y: vector.x * sin + vector.y * cos,
  };
}

export function multiplyV2(vectorA: V2, vectorB: V2): V2 {
  return {
    x: vectorA.x * vectorB.x,
    y: vectorA.y * vectorB.y,
  };
}

export function normalize(vector: V2): V2 {
  if (isZeroV2(vector)) {
    console.error('Cannot normalize a zero vector');
    return v2();
  }

  return divideV2(vector, magnitude(vector));
}

export function normalizeAndMagnitude(vector: V2): { normalize: V2; magnitude: number } {
  if (isZeroV2(vector)) {
    console.error('Cannot normalize a zero vector');
    return { normalize: v2(), magnitude: 0 };
  }
  let mag = magnitude(vector);

  if (mag === 0) {
    console.error('magnitude is 0, vector: ', vector);
    return {
      normalize: v2(),
      magnitude: mag,
    };
  }
  return {
    normalize: divideV2(vector, mag),
    magnitude: mag,
  };
}

export function isZeroV2(vector: V2): boolean {
  return Math.abs(vector.x) < 1e-10 && Math.abs(vector.y) < 1e-10;
}

export function V2IsEqual(a: V2, b: V2): boolean {
  return a.x === b.x && a.y === b.y;
}

export function limitLength(vector: V2, maxLength: number): V2 {
  if (magnitude(vector) <= maxLength) {
    return vector;
  } else {
    return scaleV2(normalize(vector), maxLength);
  }
}

export function getCenterFromCorners(corners: Corners): V2 {
  return {
    x: corners[CORNER.TOP_LEFT].x + (corners[CORNER.BOTTOM_RIGHT].x - corners[0].x) / 2,
    y: corners[CORNER.TOP_LEFT].y + (corners[CORNER.BOTTOM_RIGHT].y - corners[0].y) / 2,
  };
}

export function getWidthFromCorners(corners: Corners): number {
  return corners[CORNER.BOTTOM_RIGHT].x - corners[CORNER.TOP_LEFT].x;
}

export function getHeightFromCorners(corners: Corners): number {
  return corners[CORNER.BOTTOM_RIGHT].y - corners[CORNER.TOP_LEFT].y;
}

export function getCornerFromRect(width: number, height: number, corner: number): V2 {
  const halfW = width / 2;
  const halfH = height / 2;

  switch (corner) {
    case CORNER.TOP_LEFT: {
      return v2(-halfW, -halfH);
    }
    case CORNER.TOP_RIGHT: {
      return v2(halfW, -halfH);
    }
    case CORNER.BOTTOM_RIGHT: {
      return v2(halfW, halfH);
    }
    case CORNER.BOTTOM_LEFT: {
      return v2(-halfW, halfH);
    }
  }
}

export function lerpNumber(
  start: number,
  end: number,
  t: number,
  threshold = LERP_THRESHOLD,
): number {
  const difference = Math.abs(end - start);
  if (difference < threshold) {
    return end;
  }

  return start + (end - start) * t;
}

export function lerpV2(start: V2, end: V2, t: number, threshold = LERP_THRESHOLD): V2 {
  return v2(lerpNumber(start.x, end.x, t), lerpNumber(start.y, end.y, t, threshold));
}

export function lerpSize(start: Size, end: Size, t: number, threshold = LERP_THRESHOLD): Size {
  return toSize(
    lerpNumber(start.width, end.width, t, threshold),
    lerpNumber(start.height, end.height, t, threshold),
  );
}

export function lerpColor(start: Color, end: Color, t: number, threshold = LERP_THRESHOLD): Color {
  let newColor: Color = [0, 0, 0, 0];
  if (start.length === 3) {
    start = setColorAlpha255(start, 255);
  }
  if (end.length === 3) {
    end = setColorAlpha255(end, 255);
  }
  for (let i = 0; i < 4; i++) {
    newColor[i] = lerpNumber(start[i], end[i], t, threshold);
  }
  return newColor;
}

function lerpLineDash(
  start: number[],
  end: number[],
  t: number,
  threshold = LERP_THRESHOLD,
): number[] {
  const maxLength = Math.max(start.length, end.length);
  const interpolatedDash: number[] = [];

  for (let i = 0; i < maxLength; i++) {
    const startValue = start[i] !== undefined ? start[i] : 0; // Pad with 0 or last element
    const endValue = end[i] !== undefined ? end[i] : 0; // Pad with 0 or last element

    interpolatedDash.push(lerpNumber(startValue, endValue, t, threshold));
  }

  return interpolatedDash;
}

export function lerpShapeStyle(
  start: RenderShapeStyle,
  end: RenderShapeStyle,
  t: number,
  threshold = LERP_THRESHOLD,
): RenderShapeStyle {
  const startFadeMultiplier = start.gradient ? start.gradientFade : 0;
  const endFadeMultiplier = end.gradient ? end.gradientFade : 0;

  let result: RenderShapeStyle = {
    fillColor: lerpColor(start.fillColor, end.fillColor, t, threshold),
    strokeWeight: lerpNumber(start.strokeWeight, end.strokeWeight, t, threshold),
    strokeColor: lerpColor(start.strokeColor, end.strokeColor, t, threshold),
    lineDash: lerpLineDash(start.lineDash, end.lineDash, t, threshold),
    lineDashOffset: lerpNumber(start.lineDashOffset, end.lineDashOffset, t, threshold),
    gradient: start.gradient || end.gradient,
    gradientFade: lerpNumber(startFadeMultiplier, endFadeMultiplier, t, threshold),
    shadow: lerpShadow(start.shadow, end.shadow, t, threshold),
  };

  return result;
}

function lerpShadow(start: RenderShadow, end: RenderShadow, t: number, threshold = LERP_THRESHOLD) {
  if (!end) return null;
  if (!start) return end;

  let result: RenderShadow = {
    color: end.color, //not lerping a string
    blur: lerpNumber(start.blur, end.blur, t, threshold),
    offsetX: lerpNumber(start.offsetX, end.offsetX, t, threshold),
    offsetY: lerpNumber(start.offsetY, end.offsetY, t, threshold),
  };

  return result;
}

export function getCircleWith(radius: number, height: number): number {
  //returns the width of a circle at a particular height.
  //height is vertically centered, so 0 will be at the center of the circle,
  //thus returning the radius.

  let result = 0;
  if (radius <= 0) return 0;
  if (Math.abs(height) >= radius) return 0;
  if (height === 0) return 2 * radius;

  let p = height / radius; //height in percentage of the radius
  let angle = Math.acos(p);
  result = 2 * radius * Math.sin(angle);

  return result;
}

export function colorToHex(c: Color) {
  let hexColor = '#';
  for (let i = 0; i < c.length; i++) {
    const hex = c[i].toString(16);
    hexColor += hex.length === 1 ? '0' + hex : hex;
  }

  return hexColor;
}

export function hexToColor(hex: string): { valid: boolean; color: Color } {
  if (hex.substring(0, 1) === '#') {
    hex = hex.substring(1, hex.length);
  }
  const aRgbHex = hex.match(/.{1,2}/g);

  if (!aRgbHex) {
    return { valid: false, color: COLORS.TRANSPARENT };
  }

  if (aRgbHex.length === 3) {
    const color: Color = [
      parseInt(aRgbHex[0], 16),
      parseInt(aRgbHex[1], 16),
      parseInt(aRgbHex[2], 16),
    ];
    return { valid: true, color };
  } else if (aRgbHex.length === 4) {
    const color: Color = [
      parseInt(aRgbHex[0], 16),
      parseInt(aRgbHex[1], 16),
      parseInt(aRgbHex[2], 16),
      parseInt(aRgbHex[3], 16),
    ];
    return { valid: true, color };
  } else {
    return { valid: false, color: COLORS.BLACK_100 };
  }
}

export function colorToCssString(c: Color): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

export function getShapeDistance(entA: Entity, entB: Entity, ignoreEntBShape = false): number {
  let distA = 0;
  let distB = 0;

  let posA = entA.pos;
  let posB = entB.pos;

  if (entA.shape === Shape.CIRCLE) {
    distA = entA.radius;
  } else {
    let halfWidth = entA.width / 2;
    let halfHeight = entA.height / 2;
    let diff = subtractV2(posA, posB);
    if (diff.y === 0) {
      distA = halfWidth;
    } else if (diff.x === 0) {
      distA = halfHeight;
    } else {
      let angle = Math.atan2(diff.y, diff.x); //0.3, 65, rato = 0.001
      let one = Math.abs(halfWidth / Math.sin(Math.PI / 2 - angle));
      let two = Math.abs(halfHeight / Math.sin(angle));
      distA = Math.min(one, two);
    }
  }
  if (!ignoreEntBShape) {
    if (entB.shape === Shape.CIRCLE) {
      distB = entB.radius;
    } else {
      let halfWidth = entB.width / 2;
      let halfHeight = entB.height / 2;
      let diff = subtractV2(posB, posA);
      if (diff.y === 0) {
        distB = halfWidth;
      } else if (diff.x === 0) {
        distB = halfHeight;
      } else {
        let angle = Math.atan2(diff.y, diff.x); //0.3, 65, rato = 0.001
        let one = Math.abs(halfWidth / Math.sin(Math.PI / 2 - angle));
        let two = Math.abs(halfHeight / Math.sin(angle));
        distB = Math.min(one, two);
      }
    }
  }

  if (isNaN(distA) || isNaN(distB)) return 0;

  return distA + distB;
}

export function getRoughShapeDistance(entA: Entity, entB: Entity, ignoreEntBShape = false): number {
  let distA = 0;
  let distB = 0;

  if (entA.shape === Shape.CIRCLE) {
    distA = entA.radius;
  } else {
    distA = Math.max(entA.width, entA.height);
  }
  if (!ignoreEntBShape) {
    if (entB.shape === Shape.CIRCLE) {
      distB = entB.radius;
    } else {
      distB = Math.max(entB.width, entB.height);
    }
  }

  if (isNaN(distA) || isNaN(distB)) return 0;

  return distA + distB;
}

export function isVisibleWordType(wordType: WordType): boolean {
  return (
    wordType === WordType.Word ||
    wordType === WordType.Space ||
    wordType === WordType.Period ||
    wordType === WordType.Comma ||
    wordType === WordType.Slash ||
    wordType === WordType.ExclamationMark ||
    wordType === WordType.QuestionMark ||
    wordType === WordType.QuoteStart ||
    wordType === WordType.QuoteEnd ||
    wordType === WordType.ListItemStart
  );
}

export function getWordDebug(word: Word | Word[]): string {
  let wordArray: Word[] = [];
  if (!Array.isArray(word)) {
    wordArray.push(word);
  } else {
    wordArray = word;
  }
  let debugString = '';
  for (let i = 0; i < wordArray.length; i++) {
    if (isVisibleWordType(wordArray[i].type)) {
      debugString += wordArray[i].text;
    } else {
      debugString += WordType[wordArray[i].type];
    }
    if (i < wordArray.length - 1) {
      debugString += ', ';
    }
  }
  return debugString;
}

export function getGameObjectByEntity(manager: GameManager, entity: Entity): GameObject {
  return manager.gameObjectIdTable[entity.id];
}

//TODO(merijn): add discriminators to check type
export function isTextObject(obj: any): obj is TextObject {
  return 'textAlign' in obj;
}

export function getZeroCommand(): Command {
  let command: Command = {
    type: CommandType.NONE,
    value: '',
    args: [],
    pos: v2(),
    entity: null,
  };
  return command;
}

function getZeroShadow(): RenderShadow {
  return {
    color: `black`,
    blur: 0,
    offsetX: 0,
    offsetY: 0,
  };
}

const getRenderBaseProps = () => {
  return { zIndex: 0, next: null, label: '', contextId: 0 };
};

const getZeroRenderTransform = () => {
  return {
    position: { x: 0, y: 0 },
    positionX: 0,
    positionY: 0,
    positionLocal: { x: 0, y: 0 },
    positionLocalX: 0,
    positionLocalY: 0,
    rotation: 0,
    scale: 1,
  };
};

export function upperCaseFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function outQuad(x: number): number {
  return 1 - (1 - x) * (1 - x);
}

function inOutQuad(x: number): number {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

function outCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

function inCubic(x: number): number {
  return x * x * x;
}

function inOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function inOutCirc(x: number): number {
  return x < 0.5
    ? (1 - Math.sqrt(1 - Math.pow(2 * x, 2))) / 2
    : (Math.sqrt(1 - Math.pow(-2 * x + 2, 2)) + 1) / 2;
}

function inOutCircRev(x: number): number {
  return x > 0.5
    ? (1 - Math.sqrt(1 - Math.pow(2 * (1 - x), 2))) / 2
    : (Math.sqrt(1 - Math.pow(-2 * (1 - x) + 2, 2)) + 1) / 2;
}

function outBack(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;

  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

function outQuint(x: number): number {
  return 1 - Math.pow(1 - x, 5);
}

function inBack(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;

  return c3 * x * x * x - c1 * x * x;
}

function inOutBack(x: number): number {
  const c1 = 1.70158;
  const c2 = c1 * 1.525;

  return x < 0.5
    ? (Math.pow(2 * x, 2) * ((c2 + 1) * 2 * x - c2)) / 2
    : (Math.pow(2 * x - 2, 2) * ((c2 + 1) * (x * 2 - 2) + c2) + 2) / 2;
}

function outBounce(x: number): number {
  const n1 = 7.5625;
  const d1 = 2.75;

  if (x < 1 / d1) {
    return n1 * x * x;
  } else if (x < 2 / d1) {
    return n1 * (x -= 1.5 / d1) * x + 0.75;
  } else if (x < 2.5 / d1) {
    return n1 * (x -= 2.25 / d1) * x + 0.9375;
  } else {
    return n1 * (x -= 2.625 / d1) * x + 0.984375;
  }
}

function outElastic(x: number): number {
  const c4 = (2 * Math.PI) / 3;

  return x === 0 ? 0 : x === 1 ? 1 : Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
}

function outCirc(x: number): number {
  return Math.sqrt(1 - Math.pow(x - 1, 2));
}

function bounceCurve(t: number): number {
  // Clamp input to ensure it stays within 0-1 range
  const clampedT = Math.max(0, Math.min(1, t));

  // Parabolic formula: y = -4x^2 + 4x
  return 4 * clampedT * (1 - clampedT);
}

export const Ease = {
  outQuad,
  inOutQuad,
  outCubic,
  inCubic,
  inOutCubic,
  inOutCirc,
  inOutCircRev,
  outBack,
  outQuint,
  inBack,
  inOutBack,
  outBounce,
  outElastic,
  outCirc,
  bounceCurve,
};

export function secondsToTimeString(seconds: number, roundUp: boolean): string {
  const isNegative = seconds < 0;
  const secondsAbs = Math.abs(seconds);

  let secRounded = roundUp ? Math.ceil(secondsAbs) : Math.floor(secondsAbs);
  let minutes = Math.floor(secRounded / 60);
  let sec = Math.ceil(secRounded % 60);
  let secString = sec < 10 ? `0${sec}` : sec.toString();

  const sign = isNegative ? '-' : '';
  return `${sign}${minutes.toString()}:${secString}`;
}

export function replaceAllInText(
  text: string | string[],
  replace: string | string[],
  replaceWith: string | string[],
): string | string[] {
  let textArray: string[] = [];
  let newTextArray: string[] = [];
  let isArray = false;
  if (!Array.isArray(text)) {
    textArray.push(text);
  } else {
    isArray = true;
    textArray = text;
  }

  for (let i = 0; i < textArray.length; i++) {
    let t = textArray[i];
    let replaceArray: string[] = [];
    if (!Array.isArray(replace)) {
      replaceArray.push(replace);
    } else {
      replaceArray = replace;
    }
    let replaceWithArray: string[] = [];
    if (!Array.isArray(replaceWith)) {
      replaceWithArray.push(replaceWith);
    } else {
      replaceWithArray = replaceWith;
    }
    if (replaceArray.length !== replaceWithArray.length) {
      console.error('Not the same amount of string replace paramaters given.');
      return;
    }

    let newText = t;
    for (let i = 0; i < replaceArray.length; i++) {
      while (newText.search(replaceArray[i]) >= 0) {
        newText = newText.replace(replaceArray[i], replaceWithArray[i]);
      }
    }
    newTextArray.push(newText);
  }
  return isArray ? newTextArray : newTextArray[0];
}

export function getClosestPositionIndexAndSrqDist(positions: V2[], pos: V2): [number, number] {
  let closestDistSqr = -1;
  let closestIndex = -1;

  for (let i = 0; i < positions.length; i++) {
    let distSqr = distanceBetweenSqr(pos, positions[i]);

    if (closestDistSqr < 0 || distSqr < closestDistSqr) {
      closestDistSqr = distSqr;
      closestIndex = i;
    }
  }

  return [closestIndex, closestDistSqr];
}

export function setColorAlpha255(color: Color, alpha: number): Color {
  return [color[0], color[1], color[2], alpha];
}

export function multiplyColorAlpha255(color: Color, alpha: number): Color {
  const a = color.length === 4 ? color[3] : 255;
  const newAlpha = (a / 255) * (alpha / 255) * 255;
  return [color[0], color[1], color[2], newAlpha];
}

export function setOpacity(color: Color, opacity: number): Color {
  const a = color[3] || 255;
  const newAlpha = (a / 255) * opacity * 255;
  return [color[0], color[1], color[2], newAlpha];
}

export function isDarkColor(color: Color): boolean {
  return color[0] + color[1] + color[2] / 3 < 128;
}

export function getZeroDebugState(): DebugState {
  let state: DebugState = {
    showEntityCounts: false,
    showEntityInfo: false,
    showCmsConsole: false,
    alwaysRedraw: false,
    doDebugSkip: false,
    isDebugMode: false,
    logDirty: false,
    isDebugModeChangedThisFrame: false,
    usePools: false,
    pauseUpdate: false,
    showMapDestinations: false,
  };
  return state;
}

export function getParentRelationship(entA: Entity, entB: Entity): Entity {
  if (isParentOf(entA, entB)) return entA;
  if (isParentOf(entB, entA)) return entB;
  return null;
}

export function isParentOf(parent: Entity, child: Entity): boolean {
  let ent = child;
  while (ent.parent) {
    if (ent.parent === parent) {
      return true;
    } else {
      ent = ent.parent;
    }
  }
}

//returns whether the object is visible in this very frame
export function hasVisibleState(entityState: VisibilityState): boolean {
  return (
    entityState !== VisibilityState.NONE &&
    entityState !== VisibilityState.HIDDEN &&
    entityState !== VisibilityState.DISAPPEARING &&
    entityState !== VisibilityState.HIDING
  );
}

//returns whether the object is visible or is transforming to a visible state
export function isAppearing(entityState: VisibilityState): boolean {
  return entityState === VisibilityState.APPEARING || entityState === VisibilityState.DEFAULT;
}

//returns whether the object is invisible or is transforming to an invisible state
export function isDisappearing(entityState: VisibilityState): boolean {
  return (
    entityState === VisibilityState.DISAPPEARING ||
    entityState === VisibilityState.HIDING ||
    entityState === VisibilityState.HIDDEN ||
    entityState === VisibilityState.NONE
  );
}

export function isColorVisible(color: Color): boolean {
  if (color.length === 4 && color[3] === 0) return false;
  return true;
}

export function getSpriteCount(spriteSheet: SpriteSheet): number {
  return spriteSheet.columns * spriteSheet.rows;
}

export function checkIfMobile(): boolean {
  if (
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/webOS/i) ||
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/iPod/i) ||
    navigator.userAgent.match(/BlackBerry/i) ||
    navigator.userAgent.match(/Windows Phone/i)
  ) {
    return true;
  } else {
    return false;
  }
}

export function createPerlinController(
  minValue: number,
  maxValue: number,
  speed: number,
): PerlinController {
  const perlinNoise: PerlinController = {
    value: 0,
    minValue,
    maxValue,
    valueRange: maxValue - minValue,
    time: random(0, 1000),
    speed,
  };

  return perlinNoise;
}

export function createZeroBlob(count: number): V2[][] {
  let newBlob: V2[][] = [];

  for (let blobIndex = 0; blobIndex < count; blobIndex++) {
    newBlob.push([]);
    for (let i = 0; i < 16; i++) {
      newBlob[blobIndex].push(v2());
    }
  }

  return newBlob;
}

export function createBlob(width: number, height: number, count: number): V2[][] {
  let newBlob: V2[][] = [];

  for (let blobIndex = 0; blobIndex < count; blobIndex++) {
    let blobPoints: V2[] = [];

    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const minOffset = -5;
    const maxOffset = 5;

    {
      let list: V2[] = [];
      const min = -halfWidth + 0.1 * width;
      const max = halfWidth - 0.1 * width;
      for (let i = 0; i < 5; i++) {
        const x = random(min, max);
        const y = -halfHeight + random(minOffset, maxOffset);
        list.push(v2(x, y));
      }
      list.sort((a, b) => a.x - b.x);
      blobPoints.push(...list);
    }

    {
      const min = -halfHeight + 0.1 * height;
      const max = halfHeight - 0.1 * height;
      let list: V2[] = [];
      for (let i = 0; i < 3; i++) {
        const x = halfWidth + random(minOffset, maxOffset);
        const y = random(min, max);
        list.push(v2(x, y));
      }
      list.sort((a, b) => a.y - b.y);
      blobPoints.push(...list);
    }

    {
      let list: V2[] = [];
      const min = -halfWidth + 0.1 * width;
      const max = halfWidth - 0.1 * width;
      for (let i = 0; i < 5; i++) {
        const x = random(min, max);
        const y = halfHeight + random(minOffset, maxOffset);
        list.push(v2(x, y));
      }
      list.sort((a, b) => b.x - a.x);
      blobPoints.push(...list);
    }

    {
      const min = -halfHeight + 0.1 * height;
      const max = halfHeight - 0.1 * height;
      let list: V2[] = [];
      for (let i = 0; i < 3; i++) {
        const x = -halfWidth + random(minOffset, maxOffset);
        const y = random(min, max);
        list.push(v2(x, y));
      }
      list.sort((a, b) => b.y - a.y);
      blobPoints.push(...list);
    }
    newBlob.push(blobPoints);
  }
  return newBlob;
}

export function lerpBlob(blobA: V2[][], blobB: V2[][], t) {
  if (blobA.length !== blobB.length) {
    console.error(
      'Cannot lerp, blobs have different amounts of shapes',
      blobA.length,
      blobB.length,
    );
    return blobA;
  }
  for (let x = 0; x < blobA.length; x++) {
    if (blobA[x].length !== blobB[x].length) {
      console.error(
        'Cannot lerp, blobs have different amounts of points',
        blobA[x].length,
        blobB[x].length,
      );
      return blobA;
    }
    for (let y = 0; y < blobA[x].length; y++) {
      blobA[x][y] = lerpV2(blobA[x][y], blobB[x][y], t);
    }
  }
}

export function getLongestWord(words: Word[]): Word {
  const length = words.length;

  if (length === 0) return null;

  let longestWord: Word = null;
  let longestLength = 0;

  for (let i = 0; i < length; i++) {
    const length = words[i].text.length;
    if (length > longestLength) {
      longestWord = words[i];
      longestLength = length;
    }
  }

  return longestWord;
}

export function makeDenseArray<t>(list: t[], shouldBeRemoved: (it: t) => boolean) {
  if (!list.length) return;

  for (let i = list.length - 1; i >= 0; i--) {
    let e = list[i];

    if (shouldBeRemoved(e)) {
      removeDenseArrayIndex(list, i);
    }
  }
}

//removes array item by taking the last item in the list and move it
//to the index, then decreases the array length with 1;
//NOTE: when used in for loops, LOOP BACKWARDS!
export function removeDenseArrayIndex<t>(list: t[], index: number): number {
  const length = list.length;

  if (length > 1) {
    list[index] = list[length - 1];
  }
  list.length = length - 1;

  return list.length;
}

export function removeDenseArrayIndexAndKeepOrder<t>(list: t[], index: number): number {
  const length = list.length;

  if (length === 0) {
  } else if (length === 1 && index === 0) {
    list.length = 0;
  } else if (index === length - 1) {
    list.length = length - 1;
  } else {
    for (let i = index; i < length - 1; i++) {
      list[i] = list[i + 1];
    }
    list.length = length - 1;
  }

  return list.length;
}

export function insertInDenseArray<t>(list: t[], insert: t[], index: number) {
  const offset = insert.length;

  if (index < 0) return;
  if (index > list.length) index = list.length;

  for (let i = list.length - 1; i >= 0; i--) {
    if (i >= index) {
      list[i + offset] = list[i];
    }
  }

  for (let i = 0; i < offset; i++) {
    list[index + i] = insert[i];
  }
}

export function replaceInDenseArray<t>(list: t[], insert: t[], index: number) {
  const offset = insert.length - 1;

  if (index < 0) return;
  if (index > list.length) index = list.length;

  for (let i = list.length - 1; i >= 0; i--) {
    if (i >= index) {
      list[i + offset] = list[i];
    }
  }

  for (let i = 0; i < insert.length; i++) {
    list[index + i] = insert[i];
  }
}

export function cloneAnchoredPos(anchoredPos: AnchoredPosition): AnchoredPosition {
  return {
    referenceId: anchoredPos.referenceId,
    relativePos: cloneV2(anchoredPos.relativePos),
    offset: cloneV2(anchoredPos.offset),
    relativePivotPos: cloneV2(anchoredPos.relativePivotPos),
  };
}

export function createAnchoredPosFromSide(side: Side, offset: V2 = { x: 0, y: 0 }) {
  switch (side) {
    case Side.ABOVE:
      return anchoredPos(v2(0.5, 0), offset, v2(0.5, 1));
    case Side.BELOW:
      return anchoredPos(v2(0.5, 1), offset, v2(0.5, 0));
    case Side.LEFT:
      return anchoredPos(v2(0, 0.5), offset, v2(1, 0.5));
    case Side.RIGHT:
      return anchoredPos(v2(1, 0.5), offset, v2(0, 0.5));
  }
}

export function cloneTextStyle(textStyle: TextObjectStyle): TextObjectStyle {
  return {
    color: [...textStyle.color],
    colorHighlight: [...textStyle.colorHighlight],
    defaultTextColor: [...textStyle.defaultTextColor],
    fontSizeEm: textStyle.fontSizeEm,
    lineHeightMod: textStyle.lineHeightMod,
    fontId: textStyle.fontId,
    doAutoResize: textStyle.doAutoResize,
    textAlign: textStyle.textAlign,
    padding: typeof textStyle.padding === 'number' ? textStyle.padding : [...textStyle.padding],
    roundedSides: [...textStyle.roundedSides],
    cornerRadius: [...textStyle.cornerRadius],
    alineaSpacingMod: textStyle.alineaSpacingMod,
    strokeWeight: textStyle.strokeWeight,
    strokeColor: [...textStyle.strokeColor],
  };
}

export function cloneShapeStyle(shapeStyle: RenderShapeStyle): RenderShapeStyle {
  if (!shapeStyle) {
    logger.debug('No shapestyle');
    return SHAPE_STYLES.NONE;
  }

  return {
    fillColor: [...shapeStyle.fillColor],
    strokeWeight: shapeStyle.strokeWeight,
    strokeColor: [...shapeStyle.strokeColor],
    lineDash: shapeStyle.lineDash,
    lineDashOffset: shapeStyle.lineDashOffset,
    gradientId: shapeStyle.gradientId,
    gradient: shapeStyle.gradient,
    gradientFade: shapeStyle.gradientFade,
    shadow: shapeStyle.shadow,
  };
}

export function cloneTransfromSize(transformSize: TransformSize): TransformSize {
  return {
    sizePx: cloneSize(transformSize.sizePx),
    sizeEm: cloneSize(transformSize.sizeEm),
  };
}

export function cloneTransfromFullSize(transformSize: TransformFullSize): TransformFullSize {
  return {
    width: transformSize.width,
    height: transformSize.height,
    relativeWidth: transformSize.relativeWidth,
    relativeHeight: transformSize.relativeHeight,
    padWidth: transformSize.padWidth,
    padHeight: transformSize.padHeight,
    maxWidth: transformSize.maxWidth,
    maxHeight: transformSize.maxHeight,
    maxAspect: transformSize.maxAspect,
  };
}

export function cloneTransfromRadius(transformRadius: TransformRadius): TransformRadius {
  return {
    radiusPx: transformRadius.radiusPx,
    radiusEm: transformRadius.radiusEm,
  };
}

export function getImageUriBySize(uri: string, size: ImageSize): string {
  return uri + '&size=' + size;
}

export function getCanvas2DContext(w: number, h: number): CanvasRenderingContext2D {
  const oc = document.createElement('canvas');
  oc.width = 1;
  oc.height = 1;
  const ctx = oc.getContext('2d');

  return ctx;
}

export function sortObjectEvents(a: ObjectEvent, b: ObjectEvent): number {
  if (
    'entityProps' in b.eventData &&
    'entityProps' in a.eventData &&
    a.eventData.entityProps &&
    'parentServerId' in a.eventData.entityProps
  ) {
    if (a.eventData.entityProps.parSId === b.eventData.sId) {
      return 1;
    }
  }

  if (
    'entityProps' in a.eventData &&
    'entityProps' in b.eventData &&
    b.eventData.entityProps &&
    'parentServerId' in b.eventData.entityProps
  ) {
    if (b.eventData.entityProps.parSId === a.eventData.sId) {
      return -1;
    }
  }
  return 0;
}

export function isBulletPointSentence(words: Word[]): boolean {
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (word.type === WordType.ListItemStart) return true;
    if (isVisibleWordType(word.type)) return false;
  }
}

export function areWordsVisible(words: Word[]): boolean {
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (isVisibleWordType(word.type)) return true;
  }
  return false;
}

export function degreeToRad(degree: number): number {
  return (degree * Math.PI) / 180;
}

export function ensureTrailingSlash(inputString: string): string {
  if (!inputString.endsWith('/')) {
    return inputString + '/';
  }
  return inputString;
}

export function downloadCSV(content: string, filename: string) {
  var blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });

  var link = document.createElement('a');
  if (link.download !== undefined) {
    // feature detection
    // Browsers that support HTML5 download attribute
    var url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename + '.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export function noAutoResize(style: TextObjectStyle): TextObjectStyle {
  return {
    ...style,
    doAutoResize: false,
  };
}

export function roundNumber(number: number, decimals: number = 0): number {
  const devider = decimals > 0 ? 10 ** decimals : 1;

  const round = Math.round(number * devider);

  return round / devider;
}

export function containsLink(text: string): boolean {
  const urlPattern: RegExp = /https?:\/\/[^\s/$.?#].[^\s]*/i;
  return urlPattern.test(text);
}

export function getRandomUUID(): string {
  //crypto.randomUUID could be blocked on dev setups (no https)
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  } else {
    return generateUID();
  }
}

export function generateUID() {
  // I generate the UID from two parts here
  // to ensure the random number provide enough bits.
  let firstPart = (Math.random() * 46656) | 0;
  let secondPart = (Math.random() * 46656) | 0;
  let firstPartStr = ('000' + firstPart.toString(36)).slice(-3);
  let secondPartStr = ('000' + secondPart.toString(36)).slice(-3);
  return firstPartStr + '-' + secondPartStr;
}

export function checkBlurSupport(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
) {
  const filterSupport = typeof ctx?.filter !== 'undefined';

  return filterSupport;
}

export function randomPosOnBorder(left: number, right: number, top: number, bottom: number) {
  const width = right - left;
  const height = bottom - top;

  // Calculate the perimeter of the rectangle.
  const perimeter = 2 * (width + height);

  const randomPoint = Math.random() * perimeter;

  // Determine which side of the rectangle the random point lies on.
  if (randomPoint < width) {
    // Top edge
    return { x: left + randomPoint, y: top };
  } else if (randomPoint < width + height) {
    // Right edge
    return { x: right, y: top + randomPoint - width };
  } else if (randomPoint < 2 * width + height) {
    // Bottom edge
    return { x: right - (randomPoint - width - height), y: bottom };
  } else {
    // Left edge
    return { x: left, y: bottom - (randomPoint - 2 * width - height) };
  }
}

export function getTimezoneName() {
  const today = new Date();
  const short = today.toLocaleDateString('en-EN');
  const full = today.toLocaleDateString('en-EN', { timeZoneName: 'long' });

  // Trying to remove date from the string in a locale-agnostic way
  const shortIndex = full.indexOf(short);
  if (shortIndex >= 0) {
    const trimmed = full.substring(0, shortIndex) + full.substring(shortIndex + short.length);

    // by this time `trimmed` should be the timezone's name with some punctuation -
    // trim it from both sides
    return trimmed.replace(/^[\s,.\-:;]+|[\s,.\-:;]+$/g, '');
  } else {
    // in some magic case when short representation of date is not present in the long one, just return the long one as a fallback, since it should contain the timezone's name
    return full;
  }
}

export function objectisEqual(a: any, b: any) {}

export function DeepClone(obj: any): any {
  return 'Deep clone is disabled';
}

export function getUserInitials(user: { firstName: string; lastName: string }): string {
  let result = '';

  if (user.firstName) {
    result = user.firstName.trim().substring(0, 1);
  }
  if (user.lastName) {
    result += user.lastName.trim().substring(0, 1);
  }

  return result;
}

export function createInitialsFromName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function isToday(date: Date): boolean {
  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

export function fullName(user: User | RoomUser): string {
  return `${user.firstName} ${user.lastName}`;
}

export function getUserProfileUriOrEmptyString(user: User, size: keyof ImageSizes): string {
  if (user.profilePicSizes && user.profilePicSizes[size]) {
    return user.profilePicSizes[size].uri;
  }

  return '';
}
